// EmailSetupTab.jsx — the mailboxes reminders send from, and the signature
// they close with.
//
// Both live in the Email Management package already; this surfaces them here
// because an operator setting up invoice reminders should not have to leave
// Invoice Management, discover another package, and come back.
//
// TWO THINGS THIS DELIBERATELY DOES NOT DO:
//
//   * It does not create mailboxes. Adding one needs IMAP/SMTP hosts, ports
//     and credentials — a real form that already exists in Email Management.
//     Duplicating it here would mean two forms to keep in step. This links out.
//   * It does not add a second signature. The signature is
//     BrandSettings.emailSignature, the SAME field emailSendProcessor already
//     appends to every outbound email. Editing it here edits the one the
//     reminders actually use; a per-mailbox signature would have been a
//     competing system with no rule for which wins.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post, globalPut } from "../../api";
import { updateBrandSettings, getBrandSettings } from "../../api/brands/brandSettings";
import { getBrandStripeAccount, setBrandStripeAccount } from "../../api/invoices/invoices";

const STATUS_TONE = {
  connected: { bg: '#e8f5ee', fg: '#1b7a44', label: 'Connected' },
  connecting: { bg: '#fff6e5', fg: '#8a5a00', label: 'Connecting' },
  error: { bg: '#fdecec', fg: '#a12626', label: 'Error' },
  inactive: { bg: '#f1f2f4', fg: '#5b6670', label: 'Not connected' },
};

function StatusPill({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.inactive;
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
      {t.label}
    </span>
  );
}

export default function EmailSetupTab({ can, brands }) {
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [testing, setTesting] = useState({});

  // Signature is per BRAND, so the editor needs one selected.
  const [brandId, setBrandId] = useState('');
  const [settings, setSettings] = useState(null);
  const [signature, setSignature] = useState('');
  const [savingSig, setSavingSig] = useState(false);

  // The brand's Stripe account. Write-only: the server reports whether a key is
  // configured and which account it belongs to, never the key.
  const [stripe, setStripe] = useState({ configured: false, label: '', updatedAt: null });
  const [stripeKey, setStripeKey] = useState('');
  const [savingStripe, setSavingStripe] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { const r = await get('/api/email/accounts'); setAccounts((r && r.data) || []); }
    catch (e) { setErr((e && e.message) || 'Could not load mailboxes'); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load the brand's settings when the picker changes.
  // Whether this brand has a Stripe account, asked whenever the picker moves.
  useEffect(() => {
    setStripeKey('');
    setStripe({ configured: false, label: '', updatedAt: null });
    if (!brandId) return undefined;
    let live = true;
    (async () => {
      try {
        const r = await getBrandStripeAccount(brandId);
        const d = (r && r.data) || {};
        if (live) setStripe({ configured: !!d.configured, label: d.label || '', updatedAt: d.updatedAt || null });
      } catch (_) { /* the panel shows "not connected", which is the safe read */ }
    })();
    return () => { live = false; };
  }, [brandId]);

  useEffect(() => {
    if (!brandId) { setSettings(null); setSignature(''); return; }
    const brand = (brands || []).find((b) => String(b._id || b.id) === String(brandId));
    const settingsId = brand && (brand.brandSettings || brand.brandSettings_id);
    if (!settingsId) { setSettings(null); setSignature(''); return; }
    (async () => {
      try {
        const r = await getBrandSettings(settingsId);
        const s = (r && (r.data || r)) || null;
        setSettings(s);
        setSignature((s && s.emailSignature) || '');
      } catch (_) { setSettings(null); }
    })();
  }, [brandId, brands]);

  const unconnected = useMemo(() => accounts.filter((a) => a.status !== 'connected'), [accounts]);

  const testAccount = async (a) => {
    setTesting((p) => ({ ...p, [a._id]: true })); setErr(''); setNote('');
    try {
      const r = await post(`/api/email/accounts/${a._id}/test`, {}, false);
      const ok = r && (r.success !== false) && !(r.data && r.data.error);
      setNote(ok ? `${a.name || a.fromAddress}: connection OK` : `${a.name || a.fromAddress}: ${(r && r.error) || 'connection failed'}`);
      await load();
    } catch (e) { setErr(`${a.name || a.fromAddress}: ${(e && e.message) || 'connection failed'}`); }
    finally { setTesting((p) => ({ ...p, [a._id]: false })); }
  };

  // Tying a mailbox to a brand is what lets the reminder pick the right
  // sender AND the right signature — the signature is looked up by brand.
  const assignBrand = async (a, newBrandId) => {
    setErr('');
    try {
      await globalPut(`/api/email/accounts/${a._id}`, { brand_id: newBrandId || null }, false);
      await load();
    } catch (e) { setErr((e && e.message) || 'Could not set the brand'); }
  };

  // The brand's accounts-receivable address. A reminder flow started without a
  // mailbox named inherits this one, so "which address chases our money" is
  // decided once per brand rather than in a dropdown every time.
  const saveInvoiceMailbox = async (accountId) => {
    if (!settings || !settings._id) return;
    setErr(''); setNote('');
    try {
      await updateBrandSettings({ _id: settings._id, invoiceMailbox_id: accountId || null });
      setSettings((s) => ({ ...s, invoiceMailbox_id: accountId || null }));
      const acc = accounts.find((a) => String(a._id) === String(accountId));
      setNote(accountId
        ? `Invoices and reminders for this brand will come from ${acc ? (acc.fromAddress || acc.name) : 'that mailbox'}.`
        : 'Cleared — new reminder flows will need a mailbox picked by hand.');
    } catch (e) { setErr((e && e.message) || 'Could not set the invoicing mailbox'); }
  };

  const saveStripeKey = async () => {
    if (!brandId || !stripeKey.trim()) return;
    setSavingStripe(true); setErr(''); setNote('');
    try {
      const r = await setBrandStripeAccount(brandId, stripeKey.trim());
      const d = (r && r.data) || {};
      setStripe({ configured: true, label: d.label || '', updatedAt: new Date().toISOString() });
      setStripeKey('');
      setNote(`Stripe connected${d.label ? ` — ${d.label}` : ''}${d.live === false ? ' (TEST key — links will not take real money)' : ''}. Invoices for this brand can be given a payment link.`);
    } catch (e) {
      // The server checked the key against Stripe; whatever it said is the
      // useful sentence here, not "could not save".
      setErr((e && (e.error || e.message)) || 'Could not save the Stripe key');
    } finally { setSavingStripe(false); }
  };

  const clearStripeKey = async () => {
    if (!brandId) return;
    setSavingStripe(true); setErr(''); setNote('');
    try {
      await setBrandStripeAccount(brandId, '');
      setStripe({ configured: false, label: '', updatedAt: null });
      setNote('Stripe key removed — this brand\'s invoices will go out without a payment link.');
    } catch (e) { setErr((e && (e.error || e.message)) || 'Could not remove the Stripe key'); }
    finally { setSavingStripe(false); }
  };

  const saveSignature = async () => {
    if (!settings || !settings._id) return;
    setSavingSig(true); setErr(''); setNote('');
    try {
      await updateBrandSettings({ _id: settings._id, emailSignature: signature });
      setNote('Signature saved — reminders from this brand will close with it.');
    } catch (e) { setErr((e && e.message) || 'Could not save the signature'); }
    finally { setSavingSig(false); }
  };

  return (
    <div>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {note && <div className="alert alert-info" style={{ fontSize: 13 }}>{note}</div>}

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h5 className="mb-0" style={{ fontSize: 15 }}>Mailboxes reminders send from</h5>
        <a className="acc-btn" href="/email-management/accounts">+ Add a mailbox</a>
      </div>

      {unconnected.length > 0 && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          {unconnected.length} mailbox{unconnected.length === 1 ? '' : 'es'} not connected. A reminder cannot send from one until it connects.
        </div>
      )}

      {busy && accounts.length === 0 && <div className="cm-empty">Loading…</div>}
      {!busy && accounts.length === 0 && (
        <div className="cm-empty">No mailboxes yet. Add one in Email Management, then tie it to a brand here.</div>
      )}

      {accounts.length > 0 && (
        <table className="cm-table" style={{ marginBottom: 26 }}>
          <thead>
            <tr><th>Mailbox</th><th>From</th><th>Status</th><th>Brand</th><th /></tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a._id}>
                <td>{a.name || '—'}</td>
                <td style={{ color: '#6b7a86' }}>{a.fromAddress || '—'}</td>
                <td><StatusPill status={a.status} /></td>
                <td>
                  <select
                    className="form-control"
                    style={{ minWidth: 170, fontSize: 13 }}
                    value={a.brand_id || ''}
                    disabled={!can('INVOICE_EDIT')}
                    onChange={(e) => assignBrand(a, e.target.value)}
                  >
                    <option value="">Company-wide (no brand)</option>
                    {(brands || []).map((b) => (
                      <option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="acc-btn"
                    style={{ fontSize: 11, padding: '2px 8px' }}
                    disabled={!!testing[a._id]}
                    onClick={() => testAccount(a)}
                  >
                    {testing[a._id] ? 'Connecting…' : (a.status === 'connected' ? 'Re-test' : 'Connect')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Both settings below are per BRAND, so the picker governs both. */}
      <h5 className="mb-1" style={{ fontSize: 15 }}>Per-brand settings</h5>
      <select
        className="form-control mb-3"
        style={{ maxWidth: 320 }}
        value={brandId}
        onChange={(e) => setBrandId(e.target.value)}
      >
        <option value="">Pick a brand…</option>
        {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name}</option>))}
      </select>
      {brandId && !settings && <div className="cm-empty">This brand has no settings record yet.</div>}

      <h5 className="mb-1" style={{ fontSize: 15 }}>Invoicing mailbox</h5>
      <p style={{ fontSize: 13, color: '#6b7a86' }}>
        The brand's accounts-receivable address. New reminder loops inherit it,
        so money is never chased from a marketing mailbox by accident.
      </p>
      {settings && (
        <select
          className="form-control mb-3"
          style={{ maxWidth: 420 }}
          value={settings.invoiceMailbox_id || ''}
          disabled={!can('INVOICE_EDIT')}
          onChange={(e) => saveInvoiceMailbox(e.target.value)}
        >
          <option value="">No invoicing mailbox — pick one per loop</option>
          {accounts
            .filter((a) => !a.brand_id || String(a.brand_id) === String(brandId))
            .map((a) => (
              <option key={a._id} value={a._id}>
                {a.fromAddress || a.name}{a.status !== 'connected' ? ' (not connected)' : ''}
              </option>
            ))}
        </select>
      )}

      {/* THE BRAND'S STRIPE ACCOUNT.
          There was no way to set one anywhere in the product: one brand's key
          had been written into the database by hand and every other brand had
          none, which is why pressing "Create link" on a Free Builder invoice
          did nothing at all. Each brand bills into its OWN Stripe account —
          BidLight, Prigmar and Free Builder are separate accounts, and an
          invoice from one must be paid into that one. */}
      <h5 className="mb-1" style={{ fontSize: 15 }}>Stripe account (payment links)</h5>
      <p style={{ fontSize: 13, color: '#6b7a86' }}>
        The account this brand's invoices are paid into. Without it, invoices for
        this brand get no payment link and its reminders go out with the bank
        details only. The key is stored encrypted and never shown again.
      </p>
      {settings && (
        <div style={{ maxWidth: 560, marginBottom: 26 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            {stripe.configured
              ? <>Connected{stripe.label ? <> — <strong>{stripe.label}</strong></> : null}{stripe.updatedAt ? <span style={{ color: '#8a949c' }}> (set {new Date(stripe.updatedAt).toLocaleDateString()})</span> : null}</>
              : <span style={{ color: '#a15c00' }}>No Stripe account — this brand's invoices cannot be given a payment link.</span>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="form-control"
              type="password"
              autoComplete="new-password"
              placeholder={stripe.configured ? 'Paste a new secret key to replace it' : 'sk_live_…'}
              value={stripeKey}
              disabled={!can('INVOICE_EDIT')}
              onChange={(e) => setStripeKey(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <button
              className="acc-btn acc-btn-primary"
              style={{ whiteSpace: 'nowrap' }}
              disabled={savingStripe || !stripeKey.trim() || !can('INVOICE_EDIT')}
              onClick={saveStripeKey}
            >
              {savingStripe ? 'Checking…' : 'Save key'}
            </button>
            {stripe.configured && (
              <button
                className="acc-btn"
                style={{ whiteSpace: 'nowrap' }}
                disabled={savingStripe || !can('INVOICE_EDIT')}
                onClick={clearStripeKey}
              >
                Remove
              </button>
            )}
          </div>
          <span style={{ fontSize: 11, color: '#8a949c' }}>
            Checked against Stripe before it is saved — a key that does not work
            is refused here rather than discovered later as a reminder that went
            out with no way to pay.
          </span>
        </div>
      )}

      <h5 className="mb-1" style={{ fontSize: 15 }}>Email signature</h5>
      <p style={{ fontSize: 13, color: '#6b7a86' }}>
        Appended to every email this brand sends, reminders included. Tokens like
        {' '}<code>{'{{ownerName}}'}</code>, <code>{'{{ownerTitle}}'}</code>,{' '}
        <code>{'{{companyWebsite}}'}</code> are filled in at send time.
      </p>

      {settings && (
        <>
          <textarea
            className="form-control"
            rows={6}
            value={signature}
            placeholder={'{{ownerName}}\n{{ownerTitle}}\n{{companyWebsite}}'}
            onChange={(e) => setSignature(e.target.value)}
          />
          <div className="d-flex justify-content-end mt-2">
            <button className="acc-btn acc-btn-primary" onClick={saveSignature} disabled={savingSig || !can('INVOICE_EDIT')}>
              {savingSig ? 'Saving…' : 'Save signature'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
